import { formatCost } from "@/lib/money";
import { SkeletonTable } from "@/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { Dimension } from "@gram/client/models/components/queryfilter.js";
import { type QueryRow } from "@gram/client/models/components/queryrow.js";
import { Box, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  ClaudeCodeIcon,
  CodexIcon,
  GeminiIcon,
  AgentProviderIcon,
} from "@/components/agent-providers/AgentProviderIcon";
import { Gutter, SortHeader, SUBGRID_ROW_CLASS } from "./gridTable";
import { Sparkline } from "./Sparkline";
import { trendDirection, trendOf } from "@/components/chart/sparkline-math";
import {
  costMeasureLabel,
  ESTIMATED_COST_TOOLTIP,
  isMeteredBilling,
} from "@/components/estimated-cost-utils";
import { type Trend } from "@/components/chart/palette";
import { useTrendColors } from "@/components/chart/useSeriesColors";
import { useSeriesFocus } from "@/components/chart/seriesFocus";
import {
  displayName,
  formatWorkUnits,
  isAttributionDim,
  isDrillableValue,
  llmTokens,
} from "./taxonomy";

// Average cost per chat session for a row; 0 when there are no sessions.
function costPerSession(row: QueryRow): number {
  const chats = row.measures.totalChats ?? 0;
  return chats > 0 ? (row.measures.totalCost ?? 0) / chats : 0;
}

// Work units delivered by a row's scored sessions; 0 where nothing is scored.
function rowWorkUnits(row: QueryRow): number {
  return row.measures.totalWorkUnits ?? 0;
}

// Cost/tokens of the row's SCORED sessions per work unit — null (rendered "—")
// where the row has no scored work. Scored-only numerators keep partial
// analysis coverage from overstating the ratios (see Measures in taxonomy.ts).
function rowCostPerUnit(row: QueryRow): number | null {
  const units = rowWorkUnits(row);
  return units > 0 ? (row.measures.scoredCost ?? 0) / units : null;
}
function rowTokensPerUnit(row: QueryRow): number | null {
  const units = rowWorkUnits(row);
  return units > 0 ? (row.measures.scoredTokens ?? 0) / units : null;
}

// Bucket the cost into three bands by its position in the column's range:
// lowest third → muted green, middle → neutral (default text), highest →
// muted red — the shared TREND tokens, resolved for the current theme by the
// caller (the light pair is too dark to read on a dark canvas).
function costColor(t: number, trend: Trend): string | undefined {
  if (t >= 2 / 3) return trend.up; // high cost
  if (t <= 1 / 3) return trend.down; // low cost
  return undefined; // neutral
}

type LegendItem = { key: string; label: string; color: string };

// An info icon whose tooltip is a colour legend: bold (coloured) key + a plain
// description per line.
function LegendTooltip({
  intro,
  items,
}: {
  intro: string;
  items: LegendItem[];
}): JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger
        aria-label="Trend colour legend"
        className="text-muted-foreground inline-flex cursor-help"
      >
        <Info className="size-3.5" />
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-primary-foreground/70 mb-1">{intro}</p>
        <div className="space-y-0.5">
          {items.map((it) => (
            <div key={it.key}>
              <span className="font-semibold" style={{ color: it.color }}>
                {it.key}
              </span>{" "}
              {it.label}
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// A plain info icon + tooltip for explaining a column header or a special row.
// The trigger is a span (not the Radix default button) so it can also render
// inside the row drill buttons without nesting interactive elements.
function InfoTooltip({ text }: { text: string }): JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* stopPropagation keeps a click on the icon from activating the
            surrounding row drill button. */}
        <span
          tabIndex={0}
          aria-label={text}
          onClick={(event) => event.stopPropagation()}
          className="text-muted-foreground inline-flex shrink-0 cursor-help"
        >
          <Info className="size-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-56">{text}</TooltipContent>
    </Tooltip>
  );
}

// Why the Team-wide API Usage row exists, surfaced as an info tooltip on the
// user breakdown's empty-identity bucket.
const TEAM_WIDE_USAGE_TOOLTIP =
  "Sessions authenticated with a shared company credential (an API key or " +
  "gateway) carry no user identity, so their usage can't be attributed to an " +
  "individual and is grouped here.";

// The provider logo for a model value (claude-* → Claude, gpt-* → OpenAI, …).
function ModelIcon({
  model,
  className,
}: {
  model: string;
  className?: string;
}): JSX.Element {
  const m = model.toLowerCase();
  if (m.includes("claude")) return <ClaudeCodeIcon className={className} />;
  if (m.includes("gpt") || m.includes("openai") || /\bo[1-4]\b/.test(m)) {
    return <CodexIcon className={className} />;
  }
  if (m.includes("gemini")) return <GeminiIcon className={className} />;
  return <Box className={className} />;
}

// Parent grid track template (see gridTable.tsx for the subgrid mechanism).
// Track order: gutter | name | Total Cost | % Share | Cost/session | Chats |
// Tool calls | Tokens | Trend | gutter. Name sizes to its content (min 120px so
// short names aren't cramped, capped at 24rem so long emails truncate rather
// than dominate). The 6 numeric columns are `minmax(max-content,1fr)`: never
// narrower than their content, but they grow equally to fill the row, so
// leftover width on wide viewports spreads across the columns instead of pooling
// in a dead right gutter. Trend is the one column whose header is wider than
// anything it holds, so it gets the sparkline's own width as its floor instead —
// otherwise a heading pushes the whole table into horizontal scroll on a laptop
// viewport. Fixed 8px gutters keep row hover + dividers full-bleed.
const COLUMNS =
  "8px minmax(120px,24rem) repeat(6,minmax(max-content,1fr)) minmax(96px,1fr) 8px";

const PAGE_SIZE = 10;

type SortKey =
  | "name"
  | "cost"
  | "share"
  | "perSession"
  | "chats"
  | "tools"
  | "cache"
  | "tokens"
  | "trend"
  | "units"
  | "unitsShare"
  | "costPerUnit"
  | "tokensPerUnit";
type SortDir = "asc" | "desc";
type Sort = { key: SortKey; dir: SortDir };

function sortValue(
  row: QueryRow,
  key: SortKey,
  seriesByGroup: Map<string, number[]>,
  groupBy: Dimension,
): number | string {
  switch (key) {
    case "name":
      return displayName(groupBy, row.groupValue).toLowerCase();
    case "cost":
    // Share is cost ÷ a constant total, so it sorts identically to cost.
    case "share":
      return row.measures.totalCost ?? 0;
    case "perSession":
      return costPerSession(row);
    case "chats":
      return row.measures.totalChats ?? 0;
    case "tools":
      return row.measures.totalToolCalls ?? 0;
    case "cache":
      return row.measures.cacheCreationInputTokens ?? 0;
    case "tokens":
      return llmTokens(row.measures);
    case "units":
    // Units share is units ÷ a constant total, so it sorts identically.
    case "unitsShare":
      return rowWorkUnits(row);
    // Unscored rows (null ratio) sort as -1: below every real ratio on the
    // default descending order, so "no data yet" never outranks a real number.
    case "costPerUnit":
      return rowCostPerUnit(row) ?? -1;
    case "tokensPerUnit":
      return rowTokensPerUnit(row) ?? -1;
    case "trend": {
      // Group by colour first (up → flat → down), then by net change within a
      // group, so a sort never mixes a grey "flat" line among the red risers.
      const s = seriesByGroup.get(row.groupValue) ?? [];
      const dir = trendDirection(s);
      const rank = dir === "up" ? 1 : dir === "down" ? -1 : 0;
      return rank * 1e9 + trendOf(s);
    }
  }
}

function HeaderButton({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: Sort;
  onSort: (key: SortKey) => void;
}): JSX.Element {
  return (
    <SortHeader
      label={label}
      active={sort.key === sortKey}
      dir={sort.dir}
      onClick={() => onSort(sortKey)}
    />
  );
}

export type CostTableProps = {
  rows: QueryRow[];
  groupLabel: string;
  groupBy: Dimension;
  canDrill: boolean;
  onDrill: (row: QueryRow) => void;
  // Per-group daily cost series for the trend sparkline, keyed by group value.
  seriesByGroup: Map<string, number[]>;
  isLoading: boolean;
  // The view's resolved billing mode; "metered" shows real cost rather than the
  // API-rate estimate on the cost headers.
  billingMode?: string;
  // Efficiency lens: swap the cost columns for the work-units measures (work
  // units, cost/tokens per unit) and default the sort to work units. Callers
  // remount the table (key) when toggling, so the default sort re-applies.
  efficiency?: boolean;
  // Zero-row copy override — e.g. an active search should read "no matches",
  // not "no data".
  emptyMessage?: string;
};

export function CostTable({
  rows,
  groupLabel,
  groupBy,
  canDrill,
  onDrill,
  seriesByGroup,
  isLoading,
  billingMode,
  efficiency,
  emptyMessage,
}: CostTableProps): JSX.Element {
  const [sort, setSort] = useState<Sort>({
    key: efficiency ? "units" : "cost",
    dir: "desc",
  });
  const [page, setPage] = useState(0);
  // The chart above publishes the color it painted each stack in, keyed by the
  // stack's display label — the same label these rows show — so a row can wear
  // its segment's color and fade while another segment is hovered. Null when
  // the table renders without a chart beside it.
  const seriesFocus = useSeriesFocus();
  const trend = useTrendColors();
  // A confidently metered view shows real cost, so the estimate caveat is hidden.
  const showCostEstimate = !isMeteredBilling(billingMode);

  const onSort = (key: SortKey) => {
    setPage(0);
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "name" ? "asc" : "desc" },
    );
  };

  // Reset to the first page whenever the underlying data changes (drill, range).
  useEffect(() => setPage(0), [rows]);

  // Sort client-side, keeping the "Other" rollup pinned to the bottom.
  const sorted = useMemo(() => {
    const main = rows.filter((r) => r.groupValue !== "Other");
    const other = rows.filter((r) => r.groupValue === "Other");
    main.sort((a, b) => {
      const av = sortValue(a, sort.key, seriesByGroup, groupBy);
      const bv = sortValue(b, sort.key, seriesByGroup, groupBy);
      const cmp =
        typeof av === "string"
          ? av.localeCompare(bv as string)
          : (av as number) - (bv as number);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return [...main, ...other];
  }, [rows, sort, seriesByGroup, groupBy]);

  if (isLoading) return <SkeletonTable />;

  const showModelIcon = groupBy === Dimension.Model;
  const showAgentIcon = groupBy === Dimension.HookSource;
  // Attribution cuts (MCP server/tool, skill, subagent) replace the tool-calls
  // column with cache-creation tokens — the context weight they add to a session.
  const cacheMetric = isAttributionDim(groupBy);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const pageRows = sorted.slice(
    safePage * PAGE_SIZE,
    (safePage + 1) * PAGE_SIZE,
  );

  // Cost heat scale spans the real rows (excluding the "Other" rollup).
  const realCosts = sorted
    .filter((r) => r.groupValue !== "Other")
    .map((r) => r.measures.totalCost ?? 0);
  const minCost = realCosts.length ? Math.min(...realCosts) : 0;
  const maxCost = realCosts.length ? Math.max(...realCosts) : 0;
  // Denominator for % share — all rows incl. the "Other" rollup, so shares sum
  // to ~100% of the slice's cost.
  const totalCost = sorted.reduce(
    (sum, r) => sum + (r.measures.totalCost ?? 0),
    0,
  );
  // Efficiency-lens equivalents: the share column divides by total work units,
  // and the heat scale grades cost per unit (high = rose = expensive output)
  // across the rows that have any scored work.
  const totalUnits = sorted.reduce((sum, r) => sum + rowWorkUnits(r), 0);
  const realUnitCosts = sorted
    .filter((r) => r.groupValue !== "Other")
    .map(rowCostPerUnit)
    .filter((v): v is number => v !== null);
  const minUnitCost = realUnitCosts.length ? Math.min(...realUnitCosts) : 0;
  const maxUnitCost = realUnitCosts.length ? Math.max(...realUnitCosts) : 0;

  // The six measure header cells before the Trend column, by lens.
  function measureHeaders(): JSX.Element {
    if (efficiency) {
      return (
        <>
          <span className="flex items-center gap-1">
            <HeaderButton
              label="Work delivered"
              sortKey="units"
              sort={sort}
              onSort={onSort}
            />
            <InfoTooltip text="Work delivered by this slice's scored sessions, as judged by work analysis. Sessions without a score contribute nothing." />
          </span>
          <span className="flex items-center gap-1">
            <HeaderButton
              label="% Share"
              sortKey="unitsShare"
              sort={sort}
              onSort={onSort}
            />
            <InfoTooltip
              text={`Share of total work delivered across all ${groupLabel.toLowerCase()}s in this view.`}
            />
          </span>
          <span className="flex items-center gap-1">
            <HeaderButton
              label="Cost efficiency"
              sortKey="costPerUnit"
              sort={sort}
              onSort={onSort}
            />
            <InfoTooltip text="Cost relative to work delivered by scored sessions. Unscored spend is excluded, so partial analysis coverage can't overstate the ratio." />
          </span>
          <span className="flex">
            <HeaderButton
              label="Sessions"
              sortKey="chats"
              sort={sort}
              onSort={onSort}
            />
          </span>
          <span className="flex items-center gap-1">
            <HeaderButton
              label="Token efficiency"
              sortKey="tokensPerUnit"
              sort={sort}
              onSort={onSort}
            />
            <InfoTooltip text="Tokens relative to work delivered by scored sessions. Uses the billed token population, which also counts cache writes." />
          </span>
          <span className="flex items-center gap-1">
            <HeaderButton
              label={costMeasureLabel(billingMode)}
              sortKey="cost"
              sort={sort}
              onSort={onSort}
            />
            {showCostEstimate && <InfoTooltip text={ESTIMATED_COST_TOOLTIP} />}
          </span>
        </>
      );
    }
    return (
      <>
        <span className="flex items-center gap-1">
          <HeaderButton
            label={costMeasureLabel(billingMode)}
            sortKey="cost"
            sort={sort}
            onSort={onSort}
          />
          {showCostEstimate && <InfoTooltip text={ESTIMATED_COST_TOOLTIP} />}
        </span>
        <span className="flex items-center gap-1">
          <HeaderButton
            label="% Share"
            sortKey="share"
            sort={sort}
            onSort={onSort}
          />
          <InfoTooltip
            text={`Share of total cost across all ${groupLabel.toLowerCase()}s in this view.`}
          />
        </span>
        <span className="flex items-center gap-1">
          <HeaderButton
            label={costMeasureLabel(billingMode) + " / session"}
            sortKey="perSession"
            sort={sort}
            onSort={onSort}
          />
          {showCostEstimate && <InfoTooltip text={ESTIMATED_COST_TOOLTIP} />}
        </span>
        <span className="flex">
          <HeaderButton
            label="Sessions"
            sortKey="chats"
            sort={sort}
            onSort={onSort}
          />
        </span>
        <span className="flex items-center gap-1">
          {cacheMetric ? (
            <>
              <HeaderButton
                label="Tokens added"
                sortKey="cache"
                sort={sort}
                onSort={onSort}
              />
              <InfoTooltip text="Tokens this MCP server, tool, skill, or subagent adds to a session's context." />
            </>
          ) : (
            <HeaderButton
              label="Tool calls"
              sortKey="tools"
              sort={sort}
              onSort={onSort}
            />
          )}
        </span>
        <span className="flex">
          <HeaderButton
            label="Tokens"
            sortKey="tokens"
            sort={sort}
            onSort={onSort}
          />
        </span>
      </>
    );
  }

  // The six measure cells for one row, matching measureHeaders' column order.
  function measureCells(row: QueryRow): JSX.Element {
    const isOther = row.groupValue === "Other";
    if (efficiency) {
      const units = rowWorkUnits(row);
      const unitCost = rowCostPerUnit(row);
      const unitTokens = rowTokensPerUnit(row);
      const unitT =
        unitCost !== null && maxUnitCost > minUnitCost
          ? (unitCost - minUnitCost) / (maxUnitCost - minUnitCost)
          : 0.5;
      const unitColor =
        isOther || unitCost === null ? undefined : costColor(unitT, trend);
      return (
        <>
          <span className="text-left font-medium tabular-nums whitespace-nowrap">
            {formatWorkUnits(units)}
          </span>
          <span className="text-muted-foreground text-left tabular-nums whitespace-nowrap">
            {totalUnits > 0 && units > 0
              ? `${((units / totalUnits) * 100).toFixed(1)}%`
              : "—"}
          </span>
          <span
            className="text-left font-medium tabular-nums whitespace-nowrap"
            style={unitColor ? { color: unitColor } : undefined}
          >
            {unitCost !== null ? formatCost(unitCost) : "—"}
          </span>
          <span className="text-left tabular-nums whitespace-nowrap">
            {(row.measures.totalChats ?? 0).toLocaleString()}
          </span>
          <span className="text-left tabular-nums whitespace-nowrap">
            {unitTokens !== null
              ? Math.round(unitTokens).toLocaleString()
              : "—"}
          </span>
          <span className="text-muted-foreground text-left tabular-nums whitespace-nowrap">
            {formatCost(row.measures.totalCost ?? 0)}
          </span>
        </>
      );
    }
    const cost = row.measures.totalCost ?? 0;
    const costT =
      maxCost > minCost ? (cost - minCost) / (maxCost - minCost) : 0.5;
    return (
      <>
        <span
          className="text-left font-medium tabular-nums whitespace-nowrap"
          style={isOther ? undefined : { color: costColor(costT, trend) }}
        >
          {formatCost(cost)}
        </span>
        <span
          className="text-left tabular-nums whitespace-nowrap"
          style={isOther ? undefined : { color: costColor(costT, trend) }}
        >
          {totalCost > 0 ? `${((cost / totalCost) * 100).toFixed(1)}%` : "—"}
        </span>
        <span className="text-muted-foreground text-left tabular-nums whitespace-nowrap">
          {(row.measures.totalChats ?? 0) > 0
            ? formatCost(costPerSession(row))
            : "—"}
        </span>
        <span className="text-left tabular-nums whitespace-nowrap">
          {(row.measures.totalChats ?? 0).toLocaleString()}
        </span>
        <span className="text-left tabular-nums whitespace-nowrap">
          {cacheMetric
            ? (row.measures.cacheCreationInputTokens ?? 0).toLocaleString()
            : (row.measures.totalToolCalls ?? 0).toLocaleString()}
        </span>
        <span className="text-left tabular-nums whitespace-nowrap">
          {llmTokens(row.measures).toLocaleString()}
        </span>
      </>
    );
  }

  return (
    <div
      className="border-border divide-border grid gap-x-3 gap-y-0 divide-y overflow-x-auto border"
      style={{ gridTemplateColumns: COLUMNS }}
    >
      <div
        className={cn(
          "text-eyebrow grid items-center py-3.5",
          SUBGRID_ROW_CLASS,
        )}
      >
        <Gutter />
        <span className="flex">
          <HeaderButton
            label={groupLabel}
            sortKey="name"
            sort={sort}
            onSort={onSort}
          />
        </span>
        {measureHeaders()}
        <span className="flex items-center gap-1">
          {/* "Trend" alone — the period is the one the whole page is already
              set to, and the legend tooltip beside it says so. */}
          <HeaderButton
            label="Trend"
            sortKey="trend"
            sort={sort}
            onSort={onSort}
          />
          <LegendTooltip
            intro="over the selected range"
            items={[
              { key: "Green", label: "trending down", color: trend.down },
              { key: "Red", label: "trending up", color: trend.up },
              { key: "Grey", label: "no clear trend", color: trend.flat },
            ]}
          />
        </span>
        <Gutter />
      </div>

      {sorted.length === 0 ? (
        <div
          className="px-5 py-10 text-center"
          style={{ gridColumn: "1 / -1" }}
        >
          <Text className="text-muted-foreground">
            {emptyMessage ?? "No cost data for this slice."}
          </Text>
        </div>
      ) : (
        pageRows.map((row, i) => {
          const drillable = canDrill && isDrillableValue(row.groupValue);
          const seriesKey = displayName(groupBy, row.groupValue);
          const seriesStyle = seriesFocus?.styleByKey.get(seriesKey);
          // Hovering a bar segment spotlights its row: every other row fades
          // back. Rows the chart folded into "Other" (or dropped past its
          // stack limit) have no color and simply never spotlight.
          const faded =
            seriesFocus?.focusKey != null && seriesFocus.focusKey !== seriesKey;
          return (
            <button
              key={row.groupValue}
              type="button"
              // aria-disabled, not disabled: a natively disabled button gets
              // no pointer events at all, which would cost a non-drillable row
              // its hover link to the chart. The click stays guarded below.
              aria-disabled={!drillable}
              onClick={() => {
                if (drillable) onDrill(row);
              }}
              // The inverse of hovering a bar segment: spotlight this row's
              // series in the chart above and fade the other rows here.
              // A row the chart never drew (past its stack limit) has nothing
              // to spotlight, so it leaves the focus alone rather than dimming
              // everything.
              onMouseEnter={() =>
                seriesStyle && seriesFocus?.setFocusKey(seriesKey)
              }
              onMouseLeave={() => seriesFocus?.setFocusKey(null)}
              className={cn(
                "grid w-full items-center py-4 text-left text-sm transition-[background-color,opacity]",
                SUBGRID_ROW_CLASS,
                (safePage * PAGE_SIZE + i) % 2 === 1 && "bg-muted/25",
                drillable ? "hover:bg-muted cursor-pointer" : "cursor-default",
                faded && "opacity-35",
              )}
            >
              <Gutter />
              <div className="flex min-w-0 items-center gap-2">
                {/* The chart's swatch for this row, so a bar segment and its
                    row read as the same thing — hollow for the unset group,
                    exactly as the chart draws it. The slot is held even when
                    the row has no swatch, so names stay aligned down the
                    column. */}
                {seriesFocus && (
                  <span
                    aria-hidden="true"
                    className="size-2.5 shrink-0"
                    style={
                      seriesStyle?.outline
                        ? { border: `1px solid ${seriesStyle.color}` }
                        : seriesStyle
                          ? { backgroundColor: seriesStyle.color }
                          : undefined
                    }
                  />
                )}
                {showModelIcon && (
                  <ModelIcon
                    model={row.groupValue}
                    className="size-4 shrink-0"
                  />
                )}
                {showAgentIcon && (
                  <AgentProviderIcon
                    source={row.groupValue}
                    className="size-4 shrink-0"
                  />
                )}
                <span className="truncate font-medium">
                  {displayName(groupBy, row.groupValue)}
                </span>
                {groupBy === Dimension.Email && row.groupValue === "" && (
                  <InfoTooltip text={TEAM_WIDE_USAGE_TOOLTIP} />
                )}
                {drillable && (
                  <ChevronRight className="text-muted-foreground size-4 shrink-0" />
                )}
              </div>
              {measureCells(row)}
              <span className="flex">
                <Sparkline values={seriesByGroup.get(row.groupValue) ?? []} />
              </span>
              <Gutter />
            </button>
          );
        })
      )}

      {totalPages > 1 && (
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ gridColumn: "1 / -1" }}
        >
          <p className="text-muted-foreground text-sm">
            {safePage * PAGE_SIZE + 1}–
            {Math.min((safePage + 1) * PAGE_SIZE, sorted.length)} of{" "}
            {sorted.length.toLocaleString()}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous page"
              onClick={() => setPage((p) => p - 1)}
              disabled={safePage === 0}
              className="hover:bg-muted inline-flex size-8 items-center justify-center transition-colors disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next page"
              onClick={() => setPage((p) => p + 1)}
              disabled={safePage >= totalPages - 1}
              className="hover:bg-muted inline-flex size-8 items-center justify-center transition-colors disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
